import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Package } from 'lucide-react';
import { productApi } from '@/services/productApi';
import type { Product } from '@/types/product';

interface ProductSearchSelectProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

export const ProductSearchSelect: React.FC<ProductSearchSelectProps> = ({ selectedIds, onChange }) => {
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { products } = await productApi.getAllProducts();
        if (!cancelled) setAllProducts(products);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return allProducts
      .filter((p) => p.title.toLowerCase().includes(q) && !selectedIds.includes(p.id))
      .slice(0, 20);
  }, [search, allProducts, selectedIds]);

  const selectedProducts = useMemo(() => {
    const map = new Map(allProducts.map((p) => [p.id, p]));
    return selectedIds.map((id) => map.get(id)).filter((p): p is Product => !!p);
  }, [selectedIds, allProducts]);

  const handleSelect = (product: Product) => {
    onChange([...selectedIds, product.id]);
    setSearch('');
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const handleRemove = (id: string) => {
    onChange(selectedIds.filter((pid) => pid !== id));
  };

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(price);

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            className="input-field pl-9"
            placeholder={loading ? 'Ürünler yükleniyor...' : 'Ürün adı ile arayın...'}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => search.trim() && setIsOpen(true)}
            disabled={loading}
          />
        </div>

        {/* Dropdown */}
        {isOpen && filtered.length > 0 && (
          <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
            {filtered.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleSelect(product)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                {product.images?.[0] ? (
                  <img
                    src={product.images[0]}
                    alt={product.title}
                    className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400 flex-shrink-0">
                    <Package size={16} />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{product.title}</p>
                  <p className="text-xs text-gray-500">
                    {product.campaignPrice > 0 && product.campaignPrice < product.basePrice
                      ? formatPrice(product.campaignPrice)
                      : formatPrice(product.basePrice)}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {isOpen && search.trim() && filtered.length === 0 && !loading && (
          <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 shadow-lg dark:border-gray-700 dark:bg-gray-900">
            Eşleşen ürün bulunamadı
          </div>
        )}
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className="space-y-2">
          {selectedProducts.map((product, index) => (
            <div
              key={product.id}
              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-900"
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-rose-50 text-xs font-semibold text-rose-600 flex-shrink-0">
                {index + 1}
              </span>
              {product.images?.[0] ? (
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="h-10 w-10 rounded-lg object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-400 flex-shrink-0">
                  <Package size={16} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{product.title}</p>
                <p className="text-xs text-gray-500">
                  {product.campaignPrice > 0 && product.campaignPrice < product.basePrice
                    ? formatPrice(product.campaignPrice)
                    : formatPrice(product.basePrice)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(product.id)}
                className="flex-shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500 transition"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
